import { useEffect, useState } from "react";
import { PRODUCT_SECTIONS } from "../data/products.index";

const STORAGE_KEY = "customProducts";

export default function ProductSelector({
  selectedItems,
  setSelectedItems
}) {

  const [activeSection, setActiveSection] =
    useState("");

  const [customProducts, setCustomProducts] =
    useState([]);

  const [form, setForm] = useState({
    name: "",
    description: "",
    rate: ""
  });

  /* ---------- LOAD CUSTOM PRODUCTS ---------- */

  useEffect(() => {

    const saved =
      JSON.parse(
        localStorage.getItem(STORAGE_KEY)
      ) || [];

    setCustomProducts(saved);

  }, []);

  /* ---------- SAVE ---------- */

  const persist = (list) => {

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(list)
    );

    setCustomProducts(list);

  };

  /* ---------- TOGGLE PRODUCT ---------- */

  const toggleProduct = (product) => {

    const exists = selectedItems.find(
      (i) => i.id === product.id
    );

    if (exists) {

      setSelectedItems(
        selectedItems.filter(
          (i) => i.id !== product.id
        )
      );

    } else {

      setSelectedItems([
        ...selectedItems,
        {
          ...product,
          quantity: 1,
          discount: 0
        }
      ]);

    }

  };

  /* ---------- UPDATE QUANTITY ---------- */

  const updateQuantity = (
    id,
    quantity
  ) => {

    setSelectedItems(
      selectedItems.map((i) =>
        i.id === id
          ? {
              ...i,
              quantity:
                quantity < 1 ? 1 : quantity
            }
          : i
      )
    );

  };

  /* ---------- UPDATE DISCOUNT ---------- */

  const updateDiscount = (
    id,
    discount
  ) => {

    setSelectedItems(
      selectedItems.map((i) =>
        i.id === id
          ? {
              ...i,
              discount:
                discount < 0 ? 0 : discount
            }
          : i
      )
    );

  };

  /* ---------- CUSTOM PRODUCTS ---------- */

  const addCustomProduct = () => {

    if (!form.name || !form.rate)
      return;

    const newProduct = {
      id: `custom_${Date.now()}`,
      name: form.name,
      description: form.description,
      rate: Number(form.rate),
      isCustom: true
    };

    persist([
      ...customProducts,
      newProduct
    ]);

    setForm({
      name: "",
      description: "",
      rate: ""
    });

    setActiveSection("custom");

  };

  /* ---------- DELETE CUSTOM ---------- */

  const deleteCustomProduct = (id) => {

    persist(
      customProducts.filter(
        (p) => p.id !== id
      )
    );

    setSelectedItems(
      selectedItems.filter(
        (i) => i.id !== id
      )
    );

  };

  /* ---------- RENDER PRODUCT ---------- */

  const renderProduct = (product) => {

    const selected = selectedItems.find(
      (i) => i.id === product.id
    );

    const amount = selected
      ? Math.round(
          selected.rate * selected.quantity
        )
      : 0;

    const taxable = Math.round(
      amount - (selected?.discount || 0)
    );

    return (
      <div
        key={product.id}
        className={`rounded-3xl border transition-all duration-300 overflow-hidden ${
          selected
            ? "border-purple-400 bg-purple-50 shadow-md"
            : "border-slate-200 bg-white"
        }`}
      >

        <div className="p-4 sm:p-5">

          {/* TOP */}

          <div className="flex items-start gap-4">

            {/* CHECKBOX */}

            <input
              type="checkbox"
              checked={!!selected}
              onChange={() =>
                toggleProduct(product)
              }
              className="mt-1 w-5 h-5 accent-purple-600"
            />

            {/* PRODUCT INFO */}

            <div className="flex-1 min-w-0">

              <div className="flex flex-wrap items-center gap-2">

                <h3 className="font-semibold text-slate-800 text-base sm:text-lg">
                  {product.name}
                </h3>

                {product.isCustom && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    Custom
                  </span>
                )}

              </div>

              {product.description && (

                <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                  {product.description}
                </p>

              )}

              <div className="mt-3">

                <span className="text-xl font-bold text-purple-700">
                  ₹{" "}
                  {Math.round(product.rate).toLocaleString(
                    "en-IN",
                    { maximumFractionDigits: 0 }
                  )}
                </span>

              </div>

            </div>

          </div>

          {/* SELECTED OPTIONS */}

          {selected && (

            <div className="mt-5 border-t border-purple-200 pt-5">

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

                {/* QUANTITY */}

                <div>

                  <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                    Quantity
                  </label>

                  <input
                    type="number"
                    min="1"
                    value={selected.quantity}
                    onChange={(e) =>
                      updateQuantity(
                        product.id,
                        Number(
                          e.target.value
                        )
                      )
                    }
                    className="w-full border border-slate-300 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />

                </div>

                {/* DISCOUNT */}

                <div>

                  <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                    Discount
                  </label>

                  <input
                    type="number"
                    min="0"
                    value={
                      selected.discount || 0
                    }
                    onChange={(e) =>
                      updateDiscount(
                        product.id,
                        Number(
                          e.target.value
                        )
                      )
                    }
                    className="w-full border border-slate-300 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />

                </div>

                {/* TOTAL */}

                <div>

                  <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                    Taxable
                  </label>

                  <div className="h-[50px] rounded-2xl bg-green-50 border border-green-200 flex items-center px-4">

                    <span className="font-bold text-green-700">
                      ₹{" "}
                      {taxable.toLocaleString(
                        "en-IN",
                        { maximumFractionDigits: 0 }
                      )}
                    </span>

                  </div>

                </div>

              </div>

              {/* SUMMARY */}

              <div className="mt-4 flex flex-wrap gap-4 text-sm">

                <div className="bg-slate-100 rounded-xl px-3 py-2">
                  Amount:
                  <span className="font-semibold ml-2">
                    ₹{" "}
                    {amount.toLocaleString(
                      "en-IN",
                      { maximumFractionDigits: 0 }
                    )}
                  </span>
                </div>

                <div className="bg-red-50 text-red-700 rounded-xl px-3 py-2">
                  Discount:
                  <span className="font-semibold ml-2">
                    ₹{" "}
                    {Math.round(
                      selected.discount || 0
                    ).toLocaleString(
                      "en-IN",
                      { maximumFractionDigits: 0 }
                    )}
                  </span>
                </div>

              </div>

            </div>

          )}

          {/* DELETE */}

          {product.isCustom && (

            <div className="mt-4 pt-4 border-t border-slate-200 flex justify-end">

              <button
                onClick={() =>
                  deleteCustomProduct(
                    product.id
                  )
                }
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Delete Product
              </button>

            </div>

          )}

        </div>

      </div>
    );

  };

  /* ---------- ACTIVE PRODUCTS ---------- */

  const activeProducts =
    activeSection === "custom"
      ? customProducts
      : PRODUCT_SECTIONS.find(
          (s) =>
            s.key === activeSection
        )?.products || [];

  return (
    <div className="space-y-6">

      {/* HEADER */}

      <div>

        <h2 className="text-2xl font-bold text-slate-800">
          Products
        </h2>

        <p className="text-slate-500 mt-1">
          Select products and configure
          quantity & discounts
        </p>

      </div>

      {/* SECTION TABS */}

      <div className="flex flex-wrap gap-3">

        {PRODUCT_SECTIONS.map(
          (section) => (

            <button
              key={section.key}
              onClick={() =>
                setActiveSection(
                  section.key
                )
              }
              className={`px-5 py-3 rounded-2xl border text-sm font-semibold transition-all duration-300 ${
                activeSection ===
                section.key
                  ? "bg-purple-600 text-white border-purple-600 shadow-md"
                  : "bg-white border-slate-200 text-slate-700 hover:border-purple-300"
              }`}
            >
              {section.title}
            </button>

          )
        )}

        <button
          onClick={() =>
            setActiveSection("custom")
          }
          className={`px-5 py-3 rounded-2xl border text-sm font-semibold transition-all duration-300 ${
            activeSection === "custom"
              ? "bg-purple-600 text-white border-purple-600 shadow-md"
              : "bg-white border-slate-200 text-slate-700 hover:border-purple-300"
          }`}
        >
          Custom
        </button>

      </div>

      {/* PRODUCTS */}

      <div className="space-y-4">

        {activeProducts.length === 0 ? (

          <div className="border border-dashed border-slate-300 rounded-3xl p-10 text-center text-slate-500">
            No products available.
          </div>

        ) : (

          activeProducts.map(
            renderProduct
          )

        )}

      </div>

      {/* CUSTOM PRODUCT */}

      {activeSection === "custom" && (

        <div className="bg-slate-50 border border-slate-200 rounded-3xl p-5 sm:p-6">

          <h3 className="text-lg font-semibold text-slate-800 mb-5">
            Add Custom Product
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

            <input
              placeholder="Product Name"
              value={form.name}
              onChange={(e) =>
                setForm({
                  ...form,
                  name:
                    e.target.value
                })
              }
              className="border border-slate-300 rounded-2xl px-4 py-3"
            />

            <input
              placeholder="Description"
              value={form.description}
              onChange={(e) =>
                setForm({
                  ...form,
                  description:
                    e.target.value
                })
              }
              className="border border-slate-300 rounded-2xl px-4 py-3"
            />

            <input
              type="number"
              placeholder="Rate"
              value={form.rate}
              onChange={(e) =>
                setForm({
                  ...form,
                  rate:
                    e.target.value
                })
              }
              className="border border-slate-300 rounded-2xl px-4 py-3"
            />

            <button
              onClick={addCustomProduct}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-semibold transition-all duration-300"
            >
              Add Product
            </button>

          </div>

        </div>

      )}

    </div>
  );
}